import {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type SVGProps,
} from "react";
import {
  FiAward,
  FiCalendar,
  FiChevronDown,
  FiCheckCircle,
  FiFlag,
  FiImage,
  FiMapPin,
  FiSettings,
  FiTag,
} from "react-icons/fi";
import AppLayout from "../../../shared/components/AppLayout";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import Toast from "../../../shared/components/Toast";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { useAuth } from "../../auth/shared/hooks/useAuth";
import {
  getOrganizerVisualIdentity,
  getPublicOrganizerVisualIdentity,
  listUsers,
  saveOrganizerVisualIdentity,
} from "../../auth/shared/services/authService";
import { AuthUser } from "../../auth/shared/types/auth";
import {
  createChallenge,
  syncChallengeEvent,
} from "../../challenges/services/challengeAdminService";
import { saveTournamentScheduleConfig } from "../services/eventRegistrationAdminService";
import {
  ChallengeCategoryCode,
  ChallengeCategoryGender,
  ChallengeGameType,
  ChallengeModel,
  ChallengeType,
  EventIdentityMode,
} from "../../challenges/types/challengeAdmin";
import type { ChallengeEventDraft } from "../../challenges/types/challengeDraft";
import { ADMIN_TOAST_STORAGE_KEY } from "../constants/adminStorageKeys";
import {
  normalizeTournamentShirtSizes,
} from "../utils/tournamentCategories";
import {
  MIN_EVENT_AMOUNT_CENTS,
  parseAmountToCents,
} from "../utils/adminChallengeEditor";
import { normalizeChallengeEventDeadlineForApi } from "../utils/adminChallengeEventSync";
import {
  createScheduleDraft,
  schedulePayloadFromDraft,
} from "../utils/tournamentOperations";
import TournamentCategoryModal from "../components/TournamentCategoryModal";
import {
  AdminChallengeEventAmountField,
  AdminChallengeEventImageField,
  AdminChallengeShirtSettingsFields,
} from "../components/AdminChallengeEventSettingsFields";
import { useAdminCreateTournamentCategories } from "../hooks/useAdminCreateTournamentCategories";
import AdminCreateChallengeScheduleFields from "../components/AdminCreateChallengeScheduleFields";
import AdminCreateChallengeCategoryFields from "../components/AdminCreateChallengeCategoryFields";
import AdminChallengeCustomSettingsFields from "../components/AdminChallengeCustomSettingsFields";
import AdminCreateChallengeOrganizationFields from "../components/AdminCreateChallengeOrganizationFields";
import AdminEventIdentityFields from "../components/AdminEventIdentityFields";
import AdminCreateTournamentCategoriesSection from "../components/AdminCreateTournamentCategoriesSection";
import { AdminTournamentScheduleSettingsPanel } from "../components/AdminTournamentSettingsPanel";
import AdminVenueFields from "../components/AdminVenueFields";
import TournamentAdvancedSettingsFields from "../../tournamentAvailability/components/TournamentAdvancedSettingsFields";
import {
  availabilityRestrictionTypesFromPeriods,
  parseAvailabilityPeriodLines,
} from "../../tournamentAvailability/tournamentAvailabilitySettings";
import {
  categoryGenderLabel,
  categoryLabel,
  challengeTypeLabel,
} from "../../challenges/utils/challengeLabels";
import { normalizePocoCourtsCount } from "../../challenges/utils/pocoSchedule";
import "./AdminPage.css";

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const AwardIcon = FiAward as unknown as IconLike;
const CalendarIcon = FiCalendar as unknown as IconLike;
const ChevronDownIcon = FiChevronDown as unknown as IconLike;
const CheckCircleIcon = FiCheckCircle as unknown as IconLike;
const FlagIcon = FiFlag as unknown as IconLike;
const ImageIcon = FiImage as unknown as IconLike;
const MapPinIcon = FiMapPin as unknown as IconLike;
const SettingsIcon = FiSettings as unknown as IconLike;
const TagIcon = FiTag as unknown as IconLike;

const CHALLENGE_TYPES_ONLY_CHALLENGES: ChallengeType[] = [
  "challenge",
  "super8",
  "poco",
];
const CHALLENGE_CATEGORIES: ChallengeCategoryCode[] = [
  "7a",
  "6a",
  "5a",
  "4a",
  "3a",
  "2a",
  "open",
  "free",
];
const CATEGORY_GENDERS: ChallengeCategoryGender[] = [
  "mixed",
  "open",
  "male",
  "female",
];

type AdminCreateChallengePageProps = {
  mode?: "challenge" | "tournament";
};

type AdminEventFormSectionProps = {
  children: ReactNode;
  className?: string;
  icon: ReactNode;
  id: string;
  isOpen: boolean;
  onToggle: (id: string) => void;
  status: "complete" | "pending" | "attention";
  title: string;
  subtitle?: string;
};

function parseDateTime(value?: string | null) {
  const date = new Date(String(value || "").trim());
  return Number.isNaN(date.getTime()) ? null : date;
}

function AdminEventFormSection({
  children,
  className = "",
  icon,
  id,
  isOpen,
  onToggle,
  status,
  title,
  subtitle,
}: AdminEventFormSectionProps) {
  const contentId = `admin-event-section-${id}`;
  const statusLabel =
    status === "complete"
      ? siteTexts.admin.createChallengePage.sectionStatus.complete
      : status === "attention"
        ? siteTexts.admin.createChallengePage.sectionStatus.attention
        : siteTexts.admin.createChallengePage.sectionStatus.pending;
  return (
    <section
      className={`admin-event-form-card admin-event-form-accordion is-${status}${
        isOpen ? " is-open" : ""
      } admin-event-form-section-${id} ${className}`.trim()}
    >
      <button
        type="button"
        className="admin-event-form-card-header"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => onToggle(id)}
      >
        <span className="admin-event-form-card-icon" aria-hidden="true">{icon}</span>
        <span className="admin-event-form-card-heading">
          <strong>{title}</strong>
          {subtitle && <small>{subtitle}</small>}
        </span>
        <span className={`admin-event-form-status admin-event-form-status--${status}`}>
          {statusLabel}
        </span>
        <span className="admin-event-form-chevron" aria-hidden="true">
          <ChevronDownIcon />
        </span>
      </button>
      <div
        id={contentId}
        className="admin-event-form-card-content"
        hidden={!isOpen}
      >
        {children}
      </div>
    </section>
  );
}

export default function AdminCreateChallengePage({
  mode = "challenge",
}: AdminCreateChallengePageProps) {
  const t = siteTexts.admin;
  const createTexts = t.createChallengePage;
  const { isAdmin, isStaff, token, user } = useAuth();
  const isTournamentMode = mode === "tournament";

  const [toast, setToast] = useState<{
    message: string;
    variant: "success" | "error";
  } | null>(null);
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeCreateSection, setActiveCreateSection] = useState(
    isTournamentMode ? "general" : "event",
  );
  const [organizationTouched, setOrganizationTouched] = useState(false);
  const [shirtSettingsTouched, setShirtSettingsTouched] = useState(false);
  const [shirtSettingsChoice, setShirtSettingsChoice] =
    useState<"" | "true" | "false">("");
  const [newChallengeName, setNewChallengeName] = useState("");
  const [newChallengeScheduledAt, setNewChallengeScheduledAt] =
    useState<Date | null>(null);
  const [newChallengeScheduledEndAt, setNewChallengeScheduledEndAt] =
    useState<Date | null>(null);
  const [newChallengeDescription, setNewChallengeDescription] = useState("");
  const [newChallengeCity, setNewChallengeCity] = useState("");
  const [newChallengeState, setNewChallengeState] = useState("");
  const [newChallengeAddress, setNewChallengeAddress] = useState("");
  const [newChallengeType, setNewChallengeType] = useState<ChallengeType>(
    isTournamentMode ? "tournament" : "challenge",
  );
  const [newChallengeCustomSettings, setNewChallengeCustomSettings] = useState<{
    challengeModel: ChallengeModel;
    gameType: ChallengeGameType;
    gameCount: string;
    gameDurationMinutes: string;
    courtsCount: string;
    playersCount: string;
  }>({
    challengeModel: "tnp_standard",
    gameType: "games",
    gameCount: "",
    gameDurationMinutes: "",
    courtsCount: "2",
    playersCount: "12",
  });
  const usesMultiCategoryChallenge =
    !isTournamentMode &&
    (newChallengeType === "challenge" || newChallengeType === "poco");
  const multiCategoryVariant =
    newChallengeType === "poco" ? "poco" : "challenge";
  const {
    activeTournamentCategoryKey,
    addTournamentCategory,
    editTournamentCategory,
    removeTournamentCategory,
    saveTournamentCategoryModal,
    setActiveTournamentCategoryKey,
    setTournamentCategoryModal,
    tournamentCategories,
    tournamentCategoryModal,
    tournamentTotalSlots,
    updateTournamentCategoryModalDraft,
  } = useAdminCreateTournamentCategories({
    defaultCategoryPrefix: createTexts.defaultCategoryPrefix,
    pocoCourtsCount: Number(newChallengeCustomSettings.courtsCount),
    variant: usesMultiCategoryChallenge
      ? multiCategoryVariant
      : "tournament",
  });
  const [newChallengeCategoryGender, setNewChallengeCategoryGender] =
    useState<ChallengeCategoryGender>("mixed");
  const [newChallengeCategory, setNewChallengeCategory] =
    useState<ChallengeCategoryCode>("6a");
  const [newChallengeCategoryMale, setNewChallengeCategoryMale] =
    useState<ChallengeCategoryCode>("6a");
  const [newChallengeCategoryFemale, setNewChallengeCategoryFemale] =
    useState<ChallengeCategoryCode>("6a");
  const [newChallengeVisibility, setNewChallengeVisibility] = useState<
    "official" | "organizer"
  >(isAdmin ? "official" : "organizer");
  const [newChallengeOrganizerUserId, setNewChallengeOrganizerUserId] =
    useState("");
  const [newChallengeEventIdentityMode, setNewChallengeEventIdentityMode] =
    useState<EventIdentityMode>(isAdmin ? "tnp_standard" : "organizer_photo");
  const [selectedOrganizerLogoUrl, setSelectedOrganizerLogoUrl] = useState("");
  const [selectedOrganizerLogoSaving, setSelectedOrganizerLogoSaving] =
    useState(false);
  const [newChallengeIncludeInRanking, setNewChallengeIncludeInRanking] =
    useState(true);
  const [newChallengeEventDraft, setNewChallengeEventDraft] =
    useState<ChallengeEventDraft>({
      enabled: true,
      imageUrl: "",
      amountPerPlayer: "",
      savedAmountPerPlayerCents: null,
      registrationType: "individual",
      registrationsOpen: false,
      shirtSizesEnabled: false,
      shirtSizes: [],
      shirtAvailabilityType: "none",
      shirtDeadlineAt: "",
      shirtQuantityLimit: "",
      shirtConfirmedCount: 0,
      shirtAvailableCount: null,
      availabilityRestrictionsEnabled: false,
      availabilityRestrictionAmount: "",
      availabilityRestrictionLimit: "1",
      availabilityRestrictionDeadlineAt: "",
      availabilityRestrictionTypes: ["full_day", "day_period"],
      availabilityRestrictionPeriodsText: "",
    });
  const [newTournamentScheduleDraft, setNewTournamentScheduleDraft] = useState(
    createScheduleDraft(),
  );

  useEffect(() => {
    if (!isAdmin) return;
    setNewChallengeVisibility("official");
  }, [isAdmin]);

  useEffect(() => {
    if (!isTournamentMode) return;
    setNewChallengeType("tournament");
  }, [isTournamentMode]);

  useEffect(() => {
    if (!isAdmin || !token) return;
    setUsersLoading(true);
    listUsers(token)
      .then((data) => setUsers(data))
      .catch(() =>
        setToast({
          message: createTexts.toasts.organizersLoadError,
          variant: "error",
        }),
      )
      .finally(() => setUsersLoading(false));
  }, [createTexts.toasts.organizersLoadError, isAdmin, token]);

  const organizerUsers = useMemo(() => {
    const list = users.filter(
      (user) => user.role === "organizer" || user.role === "admin",
    );
    list.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || "")),
    );
    return list;
  }, [users]);

  const selectedOrganizer = useMemo(() => {
    const organizerId = Number(newChallengeOrganizerUserId || 0);
    if (Number.isFinite(organizerId) && organizerId > 0) {
      return organizerUsers.find((item) => Number(item.id) === organizerId) || null;
    }
    return user?.role === "organizer" || user?.role === "admin" ? user : null;
  }, [newChallengeOrganizerUserId, organizerUsers, user]);
  const canUseTnpEventIdentity =
    isAdmin && newChallengeVisibility === "official";
  const canManageSelectedOrganizerLogo =
    Boolean(token) &&
    newChallengeVisibility !== "official" &&
    Number(selectedOrganizer?.id || 0) > 0 &&
    Number(user?.id || 0) === Number(selectedOrganizer?.id || 0);

  useEffect(() => {
    if (canUseTnpEventIdentity) return;
    setNewChallengeEventIdentityMode((current) =>
      current === "tnp_standard" ? "organizer_photo" : current,
    );
  }, [canUseTnpEventIdentity]);

  useEffect(() => {
    let active = true;
    if (!token || newChallengeVisibility === "official") {
      setSelectedOrganizerLogoUrl("");
      setNewChallengeEventIdentityMode("tnp_standard");
      return;
    }
    const organizerId = Number(selectedOrganizer?.id || 0);
    if (!Number.isFinite(organizerId) || organizerId <= 0) {
      setSelectedOrganizerLogoUrl("");
      setNewChallengeEventIdentityMode((current) =>
        current === "tnp_standard" ? "organizer_photo" : current,
      );
      return;
    }
    const request =
      Number(user?.id || 0) === organizerId
        ? getOrganizerVisualIdentity(token)
        : getPublicOrganizerVisualIdentity(organizerId);
    request
      .then((settings) => {
        if (!active) return;
        setSelectedOrganizerLogoUrl(settings.logoUrl || "");
        if (!settings.logoUrl) {
          setNewChallengeEventIdentityMode((current) =>
            current === "organizer_logo" ? "organizer_photo" : current,
          );
        }
      })
      .catch(() => {
        if (!active) return;
        setSelectedOrganizerLogoUrl("");
        setNewChallengeEventIdentityMode((current) =>
          current === "organizer_logo" ? "organizer_photo" : current,
        );
      });
    return () => {
      active = false;
    };
  }, [newChallengeVisibility, selectedOrganizer?.id, token, user]);

  const saveSelectedOrganizerLogo = useCallback(
    async (logoUrl: string) => {
      if (!token || !canManageSelectedOrganizerLogo) {
        setToast({
          message: createTexts.eventIdentity.logoMissing,
          variant: "error",
        });
        return;
      }
      setSelectedOrganizerLogoSaving(true);
      try {
        const currentSettings = await getOrganizerVisualIdentity(token);
        const nextSettings = await saveOrganizerVisualIdentity(
          token,
          currentSettings.isCustom
            ? {
                mode: "custom",
                colors: currentSettings.colors,
                logoUrl,
              }
            : {
                mode: "tnp_standard",
                logoUrl,
              },
        );
        setSelectedOrganizerLogoUrl(nextSettings.logoUrl || "");
        setNewChallengeEventIdentityMode("organizer_logo");
        setToast({
          message: createTexts.eventIdentity.logoSaved,
          variant: "success",
        });
      } catch (error) {
        setToast({
          message:
            error instanceof Error
              ? error.message
              : siteTexts.profile.user.visualIdentity.saveError,
          variant: "error",
        });
      } finally {
        setSelectedOrganizerLogoSaving(false);
      }
    },
    [
      canManageSelectedOrganizerLogo,
      createTexts.eventIdentity.logoMissing,
      createTexts.eventIdentity.logoSaved,
      token,
    ],
  );

  const inferredRegistrationType: "individual" | "fixed_pair" = isTournamentMode
    ? tournamentCategories.every(
        (category) => category.registrationType === "fixed_pair",
      )
      ? "fixed_pair"
      : "individual"
    : newChallengeType === "poco"
      ? "fixed_pair"
      : newChallengeType === "super8"
      ? "individual"
      : "individual";
  const pageTitle = isTournamentMode
    ? createTexts.createTournamentTitle
    : t.modal.createChallengeTitle;
  const pageSubtitle = isTournamentMode
    ? createTexts.pageSubtitleTournament
    : createTexts.pageSubtitleChallenge;
  const selectedCategoriesSummary =
    isTournamentMode || usesMultiCategoryChallenge
      ? `${tournamentCategories.length} categoria${
          tournamentCategories.length === 1 ? "" : "s"
        }`
      : newChallengeCategoryGender === "mixed" ||
          newChallengeCategoryGender === "open"
        ? `${categoryLabel(newChallengeCategoryMale)} / ${categoryLabel(
            newChallengeCategoryFemale,
          )}`
        : categoryLabel(newChallengeCategory);
  const createSummaryCards = [
    {
      icon: <FlagIcon aria-hidden="true" />,
      label: createTexts.summary.format,
      value: isTournamentMode
        ? createTexts.summary.tournament
        : challengeTypeLabel(newChallengeType),
    },
    {
      icon: <CalendarIcon aria-hidden="true" />,
      label: createTexts.summary.date,
      value: newChallengeScheduledAt
        ? newChallengeScheduledAt.toLocaleDateString("pt-BR")
        : createTexts.summary.toBeDefined,
    },
    {
      icon: <TagIcon aria-hidden="true" />,
      label: createTexts.summary.categories,
      value: selectedCategoriesSummary,
    },
    {
      icon: <MapPinIcon aria-hidden="true" />,
      label: createTexts.summary.city,
      value: newChallengeCity.trim() || createTexts.summary.toBeDefined,
    },
  ];
  const amountPerPlayerCentsPreview = parseAmountToCents(
    newChallengeEventDraft.amountPerPlayer || "",
  );
  const formattedAmount =
    Number.isFinite(Number(amountPerPlayerCentsPreview)) &&
    Number(amountPerPlayerCentsPreview) > 0
      ? new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(Number(amountPerPlayerCentsPreview) / 100)
      : "A definir";
  const summaryDateTime = newChallengeScheduledAt
    ? `${newChallengeScheduledAt.toLocaleDateString("pt-BR")} às ${String(
        newChallengeScheduledAt.getHours(),
      ).padStart(2, "0")}:${String(newChallengeScheduledAt.getMinutes()).padStart(
        2,
        "0",
      )}`
    : createTexts.summary.toBeDefined;
  const summarySlots =
    isTournamentMode || usesMultiCategoryChallenge
      ? `${tournamentTotalSlots || 0} ${createTexts.summary.slotsSuffix}`
      : createTexts.summary.definedByFormat;
  const identitySummary =
    newChallengeVisibility === "official" ||
    newChallengeEventIdentityMode === "tnp_standard"
      ? createTexts.summary.tnpStandard
      : newChallengeEventIdentityMode === "organizer_logo"
        ? createTexts.summary.organizerLogo
        : createTexts.summary.organizerPhoto;
  const rankingSummary =
    isTournamentMode || newChallengeIncludeInRanking
      ? isAdmin
        ? createTexts.summary.countsRanking
        : createTexts.summary.countsMyRanking
      : createTexts.summary.notRanking;
  const liveSummaryLabel = isTournamentMode
    ? createTexts.summary.tournamentSummary
    : createTexts.summary.challengeSummary;
  const sectionStatuses = {
    event:
      isTournamentMode ||
      (newChallengeType &&
        (newChallengeCustomSettings.challengeModel !== "custom" ||
          newChallengeCustomSettings.gameType))
        ? "complete"
        : "pending",
    organization:
      !isAdmin ||
      (organizationTouched &&
        (newChallengeVisibility === "official" ||
          Number(newChallengeOrganizerUserId || user?.id || 0) > 0))
        ? "complete"
        : "pending",
    identity:
      String(newChallengeEventDraft.imageUrl || "").trim() &&
      (newChallengeVisibility === "official" || newChallengeEventIdentityMode)
        ? "complete"
        : "pending",
    media:
      shirtSettingsChoice === "false" ||
      (shirtSettingsChoice === "true" &&
        normalizeTournamentShirtSizes(newChallengeEventDraft.shirtSizes).length > 0)
        ? "complete"
        : "pending",
    categories:
      !isTournamentMode && !usesMultiCategoryChallenge
        ? "complete"
        : tournamentCategories.length > 0
          ? "complete"
          : "pending",
    general:
      newChallengeName.trim() &&
      Number.isFinite(Number(amountPerPlayerCentsPreview)) &&
      Number(amountPerPlayerCentsPreview) >= MIN_EVENT_AMOUNT_CENTS
        ? "complete"
        : "pending",
    location:
      newChallengeCity.trim() &&
      newChallengeAddress.trim() &&
      newChallengeScheduledAt &&
      (!isTournamentMode ||
        (newChallengeScheduledEndAt &&
          newTournamentScheduleDraft.daySchedules.every(
            (day) => day.startTime && day.endTime,
          )))
        ? "complete"
        : "pending",
    courts:
      !isTournamentMode ||
      newTournamentScheduleDraft.courtNames
        .split(/\r?\n/)
        .some((item) => item.trim())
        ? "complete"
        : "pending",
  } as const;
  const onToggleCreateSection = (id: string) => {
    setActiveCreateSection((current) => (current === id ? "" : id));
  };

  const onCreateChallenge = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) return;
    if (!newChallengeName.trim()) {
      setActiveCreateSection("general");
      return;
    }
    if (!newChallengeCity.trim()) {
      setActiveCreateSection("location");
      setToast({
        message: createTexts.toasts.cityRequired,
        variant: "error",
      });
      return;
    }
    if (!newChallengeAddress.trim()) {
      setActiveCreateSection("location");
      setToast({
        message: createTexts.toasts.clubVenueRequired,
        variant: "error",
      });
      return;
    }
    const amountPerPlayerCents = parseAmountToCents(
      newChallengeEventDraft.amountPerPlayer || "",
    );
    if (
      !Number.isFinite(Number(amountPerPlayerCents)) ||
      Number(amountPerPlayerCents) < MIN_EVENT_AMOUNT_CENTS
    ) {
      setActiveCreateSection("general");
      setToast({
        message: createTexts.toasts.priceMinimum,
        variant: "error",
      });
      return;
    }
    if (!String(newChallengeEventDraft.imageUrl || "").trim()) {
      setActiveCreateSection("identity");
      setToast({
        message: createTexts.toasts.imageRequired,
        variant: "error",
      });
      return;
    }
    if (isTournamentMode && !schedulePayloadFromDraft(newTournamentScheduleDraft)) {
      setActiveCreateSection("courts");
      setToast({
        message: createTexts.toasts.scheduleRequired,
        variant: "error",
      });
      return;
    }
    if (!shirtSettingsChoice) {
      setActiveCreateSection("shirts");
      setToast({
        message: createTexts.toasts.shirtSettingsRequired,
        variant: "error",
      });
      return;
    }
    if (
      newChallengeEventDraft.shirtSizesEnabled &&
      normalizeTournamentShirtSizes(newChallengeEventDraft.shirtSizes)
        .length === 0
    ) {
      setActiveCreateSection("shirts");
      setToast({
        message: createTexts.toasts.shirtSizesRequired,
        variant: "error",
      });
      return;
    }
    if (newChallengeCustomSettings.challengeModel === "custom") {
      const isSuper8 = newChallengeType === "super8";
      const courtsCount = isSuper8
        ? null
        : newChallengeType === "poco"
          ? normalizePocoCourtsCount(newChallengeCustomSettings.courtsCount)
          : Math.floor(Number(newChallengeCustomSettings.courtsCount || 0));
      const playersCount = isSuper8
        ? 8
        : newChallengeType === "poco" && courtsCount !== null
          ? courtsCount * 4
          : Math.floor(Number(newChallengeCustomSettings.playersCount || 0));
      if (!isSuper8 && (!Number.isFinite(playersCount) || playersCount < 12)) {
        setActiveCreateSection("event");
        setToast({
          message:
            newChallengeType === "poco"
              ? createTexts.toasts.pocoMinPlayersRequired
              : createTexts.toasts.playersCountRequired,
          variant: "error",
        });
        return;
      }
      if (!isSuper8 && (!Number.isFinite(courtsCount) || courtsCount < 2)) {
        setActiveCreateSection("event");
        setToast({
          message: createTexts.toasts.courtsCountRequired,
          variant: "error",
        });
        return;
      }
      if (newChallengeType === "poco" && playersCount !== courtsCount * 4) {
        setActiveCreateSection("event");
        setToast({
          message: createTexts.toasts.pocoExactPlayersRequired,
          variant: "error",
        });
        return;
      }
      if (newChallengeType === "challenge") {
        const maxPlayersCount = Math.max(12, Number(courtsCount) * 6);
        if (playersCount > maxPlayersCount) {
          setActiveCreateSection("event");
          setToast({
            message: createTexts.toasts.challengeMaxPlayersExceeded,
            variant: "error",
          });
          return;
        }
      }
      if (newChallengeCustomSettings.gameType === "games") {
        const gameCount = Math.floor(
          Number(newChallengeCustomSettings.gameCount || 0),
        );
        if (!Number.isFinite(gameCount) || gameCount <= 0) {
          setActiveCreateSection("event");
          setToast({
            message: createTexts.toasts.gameCountRequired,
            variant: "error",
          });
          return;
        }
      }
      if (newChallengeCustomSettings.gameType === "time") {
        const minutes = Math.floor(
          Number(newChallengeCustomSettings.gameDurationMinutes || 0),
        );
        if (!Number.isFinite(minutes) || minutes <= 0) {
          setActiveCreateSection("event");
          setToast({
            message: createTexts.toasts.gameDurationRequired,
            variant: "error",
          });
          return;
        }
      }
    }
    if (
      newChallengeEventDraft.shirtSizesEnabled &&
      newChallengeEventDraft.shirtAvailabilityType === "period" &&
      !String(newChallengeEventDraft.shirtDeadlineAt || "").trim()
    ) {
      setActiveCreateSection("shirts");
      setToast({
        message: createTexts.toasts.shirtDeadlineRequired,
        variant: "error",
      });
      return;
    }
    if (
      newChallengeEventDraft.shirtSizesEnabled &&
      newChallengeEventDraft.shirtAvailabilityType === "period"
    ) {
      const shirtDeadlineAt = parseDateTime(newChallengeEventDraft.shirtDeadlineAt);
      if (!shirtDeadlineAt || shirtDeadlineAt.getTime() < Date.now()) {
        setActiveCreateSection("shirts");
        setToast({
          message: createTexts.toasts.shirtDeadlinePast,
          variant: "error",
        });
        return;
      }
    }
    if (
      newChallengeEventDraft.shirtSizesEnabled &&
      newChallengeEventDraft.shirtAvailabilityType === "quantity" &&
      (!Number.isFinite(Number(newChallengeEventDraft.shirtQuantityLimit)) ||
        Number(newChallengeEventDraft.shirtQuantityLimit) <= 0)
    ) {
      setActiveCreateSection("shirts");
      setToast({
        message: createTexts.toasts.shirtQuantityRequired,
        variant: "error",
      });
      return;
    }
    const isOfficial = isAdmin ? newChallengeVisibility === "official" : false;
    const organizerUserId = (() => {
      if (!isAdmin) return undefined;
      if (isOfficial) return null;
      const parsed = Number(newChallengeOrganizerUserId);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        const currentUserId = Number(user?.id || 0);
        return Number.isFinite(currentUserId) && currentUserId > 0
          ? Math.floor(currentUserId)
          : null;
      }
      return Math.floor(parsed);
    })();

    if (isAdmin && !isOfficial && !organizerUserId) {
      setActiveCreateSection("organization");
      setToast({
        message: isTournamentMode
          ? createTexts.toasts.organizerRequiredTournament
          : createTexts.toasts.organizerRequiredChallenge,
        variant: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      const slotLimitNumber = Math.floor(
        Number(
          isTournamentMode || usesMultiCategoryChallenge
            ? tournamentTotalSlots
            : 0,
        ),
      );
      if (
        (isTournamentMode || usesMultiCategoryChallenge) &&
        (!Number.isFinite(slotLimitNumber) || slotLimitNumber < 2)
      ) {
        setActiveCreateSection("categories");
        setToast({
          message: createTexts.toasts.invalidSlots,
          variant: "error",
        });
        setSubmitting(false);
        return;
      }
      const payloadTournamentCategories =
        isTournamentMode || usesMultiCategoryChallenge
          ? tournamentCategories
            .map((item) => {
              const registrationType: "individual" | "fixed_pair" =
                newChallengeType === "poco" ||
                item.registrationType === "fixed_pair"
                  ? "fixed_pair"
                  : "individual";
              return {
                categoryKey: String(item.categoryKey || "").trim(),
                categoryName: String(item.categoryName || "").trim(),
                courtsCount: newChallengeType === "poco" ? Number(item.courtsCount ?? newChallengeCustomSettings.courtsCount) === 4 ? 4 : 3 : null,
                categoryMale: item.categoryMale as ChallengeCategoryCode,
                categoryFemale: item.categoryFemale as ChallengeCategoryCode,
                categoryGender: item.categoryGender as ChallengeCategoryGender,
                registrationType,
                address: String(item.address || "").trim() || null,
                scheduledAt: String(item.scheduledAt || "").trim() || null,
                scheduledEndAt: usesMultiCategoryChallenge
                  ? null
                  : String(item.scheduledEndAt || "").trim() || null,
                shirtSizesEnabled: Boolean(item.shirtSizesEnabled),
                shirtSizes: item.shirtSizesEnabled
                  ? normalizeTournamentShirtSizes(item.shirtSizes)
                  : [],
                slotLimit: usesMultiCategoryChallenge
                  ? newChallengeType === "poco"
                    ? (Number(item.courtsCount ?? newChallengeCustomSettings.courtsCount) === 4
                        ? 4
                        : 3) * 2
                    : registrationType === "fixed_pair"
                      ? 6
                      : 12
                  : Math.floor(Number(item.slotLimit || 0)),
              };
            })
            .filter(
              (item) => Number.isFinite(item.slotLimit) && item.slotLimit >= 1,
            )
          : [];
      if (
        (isTournamentMode || usesMultiCategoryChallenge) &&
        payloadTournamentCategories.length === 0
      ) {
        setActiveCreateSection("categories");
        setToast({
          message: isTournamentMode
            ? createTexts.toasts.categoriesRequired
            : createTexts.toasts.challengeCategoriesRequired,
          variant: "error",
        });
        setSubmitting(false);
        return;
      }
      if (usesMultiCategoryChallenge) {
        const duplicateCategory = new Set<string>();
        for (const item of payloadTournamentCategories) {
          const categoryKey = [
            item.categoryGender,
            item.categoryMale,
            item.categoryFemale,
            item.registrationType,
          ].join(":");
          if (!item.categoryName) {
            setActiveCreateSection("categories");
            setToast({
              message: createTexts.toasts.challengeCategoryRequired,
              variant: "error",
            });
            setSubmitting(false);
            return;
          }
          if (!item.scheduledAt) {
            setActiveCreateSection("categories");
            setToast({
              message: createTexts.toasts.categoryTimeRequired,
              variant: "error",
            });
            setSubmitting(false);
            return;
          }
          if (duplicateCategory.has(categoryKey)) {
            setActiveCreateSection("categories");
            setToast({
              message: createTexts.toasts.duplicateChallengeCategory,
              variant: "error",
            });
            setSubmitting(false);
            return;
          }
          duplicateCategory.add(categoryKey);
        }
      }
      const scheduledAt =
        newChallengeScheduledAt &&
        !Number.isNaN(newChallengeScheduledAt.getTime())
          ? newChallengeScheduledAt.toISOString()
          : null;
      const scheduledEndAt =
        newChallengeScheduledEndAt &&
        !Number.isNaN(newChallengeScheduledEndAt.getTime())
          ? newChallengeScheduledEndAt.toISOString()
          : null;
      if (
        scheduledAt &&
        scheduledEndAt &&
        new Date(scheduledEndAt).getTime() < new Date(scheduledAt).getTime()
      ) {
        setActiveCreateSection("location");
        setToast({
          message: createTexts.toasts.invalidEndDate,
          variant: "error",
        });
        setSubmitting(false);
        return;
      }
      const slotLimit =
        isTournamentMode || usesMultiCategoryChallenge
          ? slotLimitNumber
          : undefined;
      const created = await createChallenge(token, {
        challengeName: newChallengeName.trim(),
        description: newChallengeDescription.trim(),
        city: newChallengeCity.trim(),
        state: newChallengeState.trim() || undefined,
        address: newChallengeAddress.trim(),
        groupName: isTournamentMode ? "tournament" : newChallengeType,
        categoryGender:
          isTournamentMode || usesMultiCategoryChallenge
            ? null
            : newChallengeCategoryGender,
        category:
          isTournamentMode ||
          usesMultiCategoryChallenge ||
          newChallengeCategoryGender === "mixed" ||
          newChallengeCategoryGender === "open"
            ? null
            : newChallengeCategory,
        categoryMale: isTournamentMode || usesMultiCategoryChallenge
          ? null
          : newChallengeCategoryGender === "mixed" ||
              newChallengeCategoryGender === "open"
            ? newChallengeCategoryMale
            : null,
        categoryFemale: isTournamentMode || usesMultiCategoryChallenge
          ? null
          : newChallengeCategoryGender === "mixed" ||
              newChallengeCategoryGender === "open"
            ? newChallengeCategoryFemale
            : null,
        super8Group: null,
        participantCpfs: [],
        pairIds: [],
        scheduledAt,
        scheduledEndAt,
        isOfficial,
        includeInRanking: isTournamentMode
          ? isOfficial
            ? isAdmin && newChallengeIncludeInRanking
            : true
          : newChallengeIncludeInRanking,
        organizerUserId: isOfficial ? null : (organizerUserId ?? null),
        eventIdentityMode: isOfficial
          ? "tnp_standard"
          : newChallengeEventIdentityMode === "tnp_standard"
            ? "organizer_photo"
            : newChallengeEventIdentityMode,
        registrationType: undefined,
        slotLimit:
          (isTournamentMode || usesMultiCategoryChallenge) &&
          Number.isFinite(slotLimit)
            ? slotLimit
            : undefined,
        challengeModel: !isTournamentMode
          ? newChallengeCustomSettings.challengeModel
          : undefined,
        gameType:
          !isTournamentMode &&
          newChallengeCustomSettings.challengeModel === "custom"
            ? newChallengeCustomSettings.gameType
            : undefined,
        gameCount:
          !isTournamentMode &&
          newChallengeCustomSettings.challengeModel === "custom" &&
          newChallengeCustomSettings.gameType === "games"
            ? Math.floor(Number(newChallengeCustomSettings.gameCount))
            : undefined,
        gameDurationMinutes:
          !isTournamentMode &&
          newChallengeCustomSettings.challengeModel === "custom" &&
          newChallengeCustomSettings.gameType === "time"
            ? Math.floor(Number(newChallengeCustomSettings.gameDurationMinutes))
            : undefined,
        courtsCount:
          !isTournamentMode &&
          (newChallengeCustomSettings.challengeModel === "custom" ||
            newChallengeType === "poco") &&
          newChallengeType !== "super8"
            ? newChallengeType === "poco"
              ? normalizePocoCourtsCount(newChallengeCustomSettings.courtsCount)
              : Math.max(2, Math.floor(Number(newChallengeCustomSettings.courtsCount)))
            : undefined,
        playersCount:
          !isTournamentMode &&
          (newChallengeCustomSettings.challengeModel === "custom" ||
            newChallengeType === "poco")
            ? newChallengeType === "super8"
              ? 8
              : newChallengeType === "poco"
                ? normalizePocoCourtsCount(
                    newChallengeCustomSettings.courtsCount,
                  ) * 4
                : Math.max(12, Math.floor(Number(newChallengeCustomSettings.playersCount)))
            : undefined,
        tournamentCategories:
          (isTournamentMode || usesMultiCategoryChallenge) &&
          payloadTournamentCategories.length > 0
            ? payloadTournamentCategories
            : undefined,
        availabilityRestrictionsEnabled: isTournamentMode
          ? Boolean(newChallengeEventDraft.availabilityRestrictionsEnabled)
          : false,
        availabilityRestrictionAmountCents:
          isTournamentMode && newChallengeEventDraft.availabilityRestrictionsEnabled
            ? parseAmountToCents(
                newChallengeEventDraft.availabilityRestrictionAmount || "",
              ) ?? 0
            : null,
        availabilityRestrictionLimit:
          isTournamentMode && newChallengeEventDraft.availabilityRestrictionsEnabled
            ? Math.max(
                1,
                Math.floor(
                  Number(newChallengeEventDraft.availabilityRestrictionLimit || 1),
                ),
              )
            : null,
        availabilityRestrictionDeadlineAt: null,
        availabilityRestrictionTypes:
          isTournamentMode && newChallengeEventDraft.availabilityRestrictionsEnabled
            ? availabilityRestrictionTypesFromPeriods(
                parseAvailabilityPeriodLines(
                  newChallengeEventDraft.availabilityRestrictionPeriodsText,
                ),
              )
            : [],
        availabilityRestrictionPeriods:
          isTournamentMode && newChallengeEventDraft.availabilityRestrictionsEnabled
            ? parseAvailabilityPeriodLines(
                newChallengeEventDraft.availabilityRestrictionPeriodsText,
              )
            : [],
      });
      await syncChallengeEvent(token, created.id, {
        enabled: true,
        imageUrl: String(newChallengeEventDraft.imageUrl || "").trim(),
        amountPerPlayerCents,
        registrationType: inferredRegistrationType,
        registrationsOpen: Boolean(newChallengeEventDraft.registrationsOpen),
        shirtSizesEnabled: Boolean(newChallengeEventDraft.shirtSizesEnabled),
        shirtSizes: newChallengeEventDraft.shirtSizesEnabled
          ? normalizeTournamentShirtSizes(newChallengeEventDraft.shirtSizes)
          : [],
        shirtAvailabilityType: newChallengeEventDraft.shirtSizesEnabled
          ? newChallengeEventDraft.shirtAvailabilityType
          : "none",
        shirtDeadlineAt:
          newChallengeEventDraft.shirtSizesEnabled &&
          newChallengeEventDraft.shirtAvailabilityType === "period"
            ? normalizeChallengeEventDeadlineForApi(
                newChallengeEventDraft.shirtDeadlineAt,
              )
            : null,
        shirtQuantityLimit:
          newChallengeEventDraft.shirtSizesEnabled &&
          newChallengeEventDraft.shirtAvailabilityType === "quantity"
            ? Math.floor(Number(newChallengeEventDraft.shirtQuantityLimit))
            : null,
      });
      if (isTournamentMode) {
        const schedulePayload = schedulePayloadFromDraft(newTournamentScheduleDraft);
        if (schedulePayload) {
          await saveTournamentScheduleConfig(token, Number(created.id), schedulePayload);
        }
      }
      sessionStorage.setItem(
        ADMIN_TOAST_STORAGE_KEY,
        JSON.stringify({
          message: isTournamentMode
            ? createTexts.toasts.tournamentSaved
            : createTexts.toasts.challengeSaved,
          variant: "success",
        }),
      );
      window.location.hash = isTournamentMode ? "#/admin/torneio" : "#/admin";
    } catch (_err) {
      setToast({
        message:
          _err instanceof Error && _err.message
            ? _err.message
            : isTournamentMode
              ? createTexts.toasts.tournamentSaveError
              : createTexts.toasts.challengeSaveError,
        variant: "error",
      });
      setSubmitting(false);
    }
  };

  if (!isStaff) {
    return (
      <AppLayout title={siteTexts.header.nav.admin}>
        <EmptyState message={t.accessDenied} />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {toast && (
        <Toast
          message={toast.message}
          variant={toast.variant}
          onClose={() => setToast(null)}
        />
      )}
      <div className="admin-content admin-create-challenge-page">
        {tournamentCategoryModal && isTournamentMode && (
          <TournamentCategoryModal
            modal={tournamentCategoryModal}
            categoryOptions={CHALLENGE_CATEGORIES}
            genderOptions={CATEGORY_GENDERS}
            onChangeDraft={updateTournamentCategoryModalDraft}
            onClose={() => setTournamentCategoryModal(null)}
            onSave={saveTournamentCategoryModal}
            categoryLabel={categoryLabel}
            categoryGenderLabel={categoryGenderLabel}
            token={token}
          />
        )}
        {tournamentCategoryModal && usesMultiCategoryChallenge && (
          <TournamentCategoryModal
            modal={tournamentCategoryModal}
            categoryOptions={CHALLENGE_CATEGORIES}
            genderOptions={CATEGORY_GENDERS}
            onChangeDraft={updateTournamentCategoryModalDraft}
            onClose={() => setTournamentCategoryModal(null)}
            onSave={saveTournamentCategoryModal}
            categoryLabel={categoryLabel}
            categoryGenderLabel={categoryGenderLabel}
            token={token}
            showCategoryTime
            categoryDate={newChallengeScheduledAt}
            categoryTimeMode="timeOnly"
            acceptLegacyPocoTime={newChallengeType === "poco"}
            lockRegistrationType={newChallengeType === "poco"}
            lockSlotLimit
          />
        )}
        <AdminSection className="admin-event-form-dashboard">
          <form
            className="admin-create-challenge-form"
            onSubmit={onCreateChallenge}
          >
            <div className="admin-event-form-hero">
              <div className="admin-event-form-hero-copy">
                <span className="admin-event-form-hero-icon" aria-hidden="true">
                  <AwardIcon />
                </span>
                <div>
                  <h2>{pageTitle}</h2>
                  <p>{pageSubtitle}</p>
                </div>
              </div>
            </div>

            <div className="admin-event-form-summary-grid" aria-label="Resumo do evento">
              {createSummaryCards.map((card) => (
                <div className="admin-event-form-summary-card" key={card.label}>
                  <span>{card.icon}</span>
                  <div>
                    <strong>{card.value}</strong>
                    <small>{card.label}</small>
                  </div>
                </div>
              ))}
            </div>

            <div className="admin-event-form-layout">
              <div className="admin-event-form-main">
            <AdminEventFormSection
              id="general"
              isOpen={activeCreateSection === "general"}
              onToggle={onToggleCreateSection}
              icon={<AwardIcon />}
              status={sectionStatuses.general}
              title={createTexts.generalInfoTitle}
              subtitle={createTexts.generalInfoDescription}
            >
              <label className="field">
                <span>
                  {isTournamentMode
                    ? createTexts.tournamentName
                    : t.labels.challengeName}
                </span>
                <input
                  type="text"
                  value={newChallengeName}
                  onChange={(event) => setNewChallengeName(event.target.value)}
                  required
                />
              </label>
              <label className="field admin-event-form-field-wide">
                <span>{t.labels.description}</span>
                <textarea
                  value={newChallengeDescription}
                  onChange={(event) =>
                    setNewChallengeDescription(event.target.value)
                  }
                  rows={4}
                />
              </label>
              <AdminChallengeEventAmountField
                challengeEventDraft={newChallengeEventDraft}
                setChallengeEventDraft={setNewChallengeEventDraft}
              />
            </AdminEventFormSection>

            <AdminEventFormSection
              id="location"
              isOpen={activeCreateSection === "location"}
              onToggle={onToggleCreateSection}
              icon={<MapPinIcon />}
              status={sectionStatuses.location}
              title={createTexts.locationScheduleTitle}
              subtitle={createTexts.locationPeriodDescription}
            >
              <AdminVenueFields
                token={token}
                address={newChallengeAddress}
                city={newChallengeCity}
                state={newChallengeState}
                onAddressChange={setNewChallengeAddress}
                onCityChange={setNewChallengeCity}
                onStateChange={setNewChallengeState}
              />
              <AdminCreateChallengeScheduleFields
                isTournamentMode={isTournamentMode}
                scheduledAt={newChallengeScheduledAt}
                scheduledEndAt={newChallengeScheduledEndAt}
                setScheduledAt={setNewChallengeScheduledAt}
                setScheduledEndAt={setNewChallengeScheduledEndAt}
              />
              {isTournamentMode && (
                <div className="admin-tournament-schedule-settings-host">
                  <AdminTournamentScheduleSettingsPanel
                    eventEndAt={newChallengeScheduledEndAt?.toISOString() || null}
                    eventStartAt={newChallengeScheduledAt?.toISOString() || null}
                    loadingSettings={false}
                    scheduleDraft={newTournamentScheduleDraft}
                    section="days"
                    setScheduleDraft={setNewTournamentScheduleDraft}
                  />
                </div>
              )}
            </AdminEventFormSection>
            {isTournamentMode && (
              <AdminEventFormSection
                id="courts"
                isOpen={activeCreateSection === "courts"}
                onToggle={onToggleCreateSection}
                icon={<SettingsIcon />}
                status={sectionStatuses.courts}
                title={createTexts.courtsTitle}
                subtitle={createTexts.courtsDescription}
              >
                <div className="admin-tournament-schedule-settings-host">
                  <AdminTournamentScheduleSettingsPanel
                    eventEndAt={newChallengeScheduledEndAt?.toISOString() || null}
                    eventStartAt={newChallengeScheduledAt?.toISOString() || null}
                    loadingSettings={false}
                    scheduleDraft={newTournamentScheduleDraft}
                    section="courts"
                    setScheduleDraft={setNewTournamentScheduleDraft}
                  />
                </div>
              </AdminEventFormSection>
            )}
            <AdminEventFormSection
              id="organization"
              isOpen={activeCreateSection === "organization"}
              onToggle={onToggleCreateSection}
              icon={<SettingsIcon />}
              status={sectionStatuses.organization}
              title={createTexts.organizationRankingTitle}
              subtitle={createTexts.organizationRankingDescription}
            >
              <AdminCreateChallengeOrganizationFields
                includeInRanking={newChallengeIncludeInRanking}
                isAdmin={isAdmin}
                isTournamentMode={isTournamentMode}
                organizerUserId={newChallengeOrganizerUserId}
                organizerUsers={organizerUsers}
                setIncludeInRanking={(value) => {
                  setOrganizationTouched(true);
                  setNewChallengeIncludeInRanking(value);
                }}
                setOrganizerUserId={(value) => {
                  setOrganizationTouched(true);
                  setNewChallengeOrganizerUserId(value);
                }}
                setVisibility={(value) => {
                  setOrganizationTouched(true);
                  setNewChallengeVisibility(value);
                }}
                usersLoading={usersLoading}
                visibility={newChallengeVisibility}
              />
            </AdminEventFormSection>

            <AdminEventFormSection
              id="identity"
              isOpen={activeCreateSection === "identity"}
              onToggle={onToggleCreateSection}
              icon={<ImageIcon />}
              status={sectionStatuses.identity}
              title={createTexts.visualIdentityTitle}
              subtitle={createTexts.visualIdentityDescription}
            >
              <AdminChallengeEventImageField
                challengeEventDraft={newChallengeEventDraft}
                setChallengeEventDraft={setNewChallengeEventDraft}
              />
              <AdminEventIdentityFields
                value={
                  newChallengeVisibility === "official"
                    ? "tnp_standard"
                    : newChallengeEventIdentityMode
                }
                onChange={setNewChallengeEventIdentityMode}
                organizerName={selectedOrganizer?.name}
                organizerAvatarUrl={selectedOrganizer?.avatarUrl}
                organizerLogoUrl={selectedOrganizerLogoUrl}
                disabled={newChallengeVisibility === "official"}
                allowTnpStandard={canUseTnpEventIdentity}
                canManageLogo={canManageSelectedOrganizerLogo}
                logoSaving={selectedOrganizerLogoSaving}
                onLogoError={(message) =>
                  setToast({ message, variant: "error" })
                }
                onLogoUpload={saveSelectedOrganizerLogo}
                hideHeader
              />
            </AdminEventFormSection>

            {(isTournamentMode || usesMultiCategoryChallenge) && (
              <AdminEventFormSection
                id="categories"
                isOpen={activeCreateSection === "categories"}
                onToggle={onToggleCreateSection}
                icon={<TagIcon />}
                status={sectionStatuses.categories}
                title={
                  usesMultiCategoryChallenge
                    ? createTexts.challengeCategoriesTitle
                    : createTexts.tournamentCategoriesTitle
                }
                subtitle={
                  usesMultiCategoryChallenge
                    ? createTexts.challengeCategoriesDescription
                    : createTexts.tournamentCategoriesDescription
                }
              >
                <AdminCreateTournamentCategoriesSection
                  activeTournamentCategoryKey={activeTournamentCategoryKey}
                  onAddCategory={addTournamentCategory}
                  onEditCategory={editTournamentCategory}
                  onRemoveCategory={removeTournamentCategory}
                  onSelectCategory={setActiveTournamentCategoryKey}
                  hideHeader
                  tournamentCategories={tournamentCategories}
                  tournamentTotalSlots={tournamentTotalSlots}
                  variant={
                    usesMultiCategoryChallenge
                      ? multiCategoryVariant
                      : "tournament"
                  }
                />
              </AdminEventFormSection>
            )}

            <AdminEventFormSection
              id="shirts"
              isOpen={activeCreateSection === "shirts"}
              onToggle={onToggleCreateSection}
              icon={<CheckCircleIcon />}
              status={sectionStatuses.media}
              title="Camisetas"
              subtitle="Configure somente tamanhos, prazo e disponibilidade."
            >
              <AdminChallengeShirtSettingsFields
                challengeEventDraft={newChallengeEventDraft}
                selectionValue={shirtSettingsChoice}
                selectionTouched={shirtSettingsTouched}
                onSelectionChange={(value) => {
                  setShirtSettingsChoice(value);
                  setShirtSettingsTouched(true);
                }}
                onSelectionTouched={() => setShirtSettingsTouched(true)}
                setChallengeEventDraft={setNewChallengeEventDraft}
              />
            </AdminEventFormSection>

            {isTournamentMode && (
              <AdminEventFormSection
                id="advanced"
                isOpen={activeCreateSection === "advanced"}
                onToggle={onToggleCreateSection}
                icon={<SettingsIcon />}
                status="complete"
                title={t.createChallengePage.advancedSettingsTitle}
                subtitle={t.createChallengePage.advancedSettingsDescription}
              >
                <TournamentAdvancedSettingsFields
                  challengeEventDraft={newChallengeEventDraft}
                  setChallengeEventDraft={setNewChallengeEventDraft}
                />
              </AdminEventFormSection>
            )}

            {!isTournamentMode && (
              <AdminEventFormSection
                id="event"
                isOpen={activeCreateSection === "event"}
                onToggle={onToggleCreateSection}
                icon={<FlagIcon />}
                status={sectionStatuses.event}
                title={createTexts.eventSettingsTitle}
                subtitle="Defina o formato, ranking e modelo do desafio."
              >
                <AdminCreateChallengeCategoryFields
                  category={newChallengeCategory}
                  categoryFemale={newChallengeCategoryFemale}
                  categoryGender={newChallengeCategoryGender}
                  categoryMale={newChallengeCategoryMale}
                  categoryOptions={CHALLENGE_CATEGORIES}
                  challengeType={newChallengeType}
                  challengeTypes={CHALLENGE_TYPES_ONLY_CHALLENGES}
                  genderOptions={CATEGORY_GENDERS}
                  setCategory={setNewChallengeCategory}
                  setCategoryFemale={setNewChallengeCategoryFemale}
                  setCategoryGender={setNewChallengeCategoryGender}
                  setCategoryMale={setNewChallengeCategoryMale}
                  setChallengeType={setNewChallengeType}
                />
                <AdminChallengeCustomSettingsFields
                  challengeType={newChallengeType}
                  value={newChallengeCustomSettings}
                  onChange={(patch) =>
                    setNewChallengeCustomSettings((prev) => ({
                      ...prev,
                      challengeModel: patch.challengeModel ?? prev.challengeModel,
                      gameType: patch.gameType ?? prev.gameType,
                      gameCount:
                        patch.gameCount === undefined
                          ? prev.gameCount
                          : String(patch.gameCount ?? ""),
                      gameDurationMinutes:
                        patch.gameDurationMinutes === undefined
                          ? prev.gameDurationMinutes
                          : String(patch.gameDurationMinutes ?? ""),
                      courtsCount:
                        patch.courtsCount === undefined
                          ? prev.courtsCount
                          : String(patch.courtsCount ?? ""),
                      playersCount:
                        patch.playersCount === undefined
                          ? prev.playersCount
                          : String(patch.playersCount ?? ""),
                    }))
                  }
                />
              </AdminEventFormSection>
            )}
              </div>
              <aside className="admin-event-live-summary" aria-label={liveSummaryLabel}>
                <span className="admin-event-live-summary-kicker">{liveSummaryLabel}</span>
                <strong>
                  {newChallengeName.trim() ||
                    (isTournamentMode ? "Novo torneio" : "Novo desafio")}
                </strong>
                <div className="admin-event-live-summary-list">
                  <span>
                    <FlagIcon aria-hidden="true" />
                    {isTournamentMode ? "Torneio" : challengeTypeLabel(newChallengeType)}
                  </span>
                  <span>
                    <CalendarIcon aria-hidden="true" />
                    {summaryDateTime}
                  </span>
                  <span>
                    <MapPinIcon aria-hidden="true" />
                    {[newChallengeAddress, newChallengeCity]
                      .map((value) => String(value || "").trim())
                      .filter(Boolean)
                      .join(" / ") || "Local a definir"}
                  </span>
                  <span>
                    <TagIcon aria-hidden="true" />
                    {selectedCategoriesSummary}
                  </span>
                  <span>
                    <SettingsIcon aria-hidden="true" />
                    {summarySlots}
                  </span>
                  <span>
                    <CheckCircleIcon aria-hidden="true" />
                    {formattedAmount}
                  </span>
                  <span>
                    <AwardIcon aria-hidden="true" />
                    {rankingSummary}
                  </span>
                  <span>
                    <ImageIcon aria-hidden="true" />
                    {identitySummary}
                  </span>
                </div>
                <div className="admin-event-live-summary-actions">
                  <Button type="submit" variant="primary" disabled={submitting}>
                    {submitting
                      ? createTexts.saving
                      : isTournamentMode
                        ? createTexts.submitTournament
                        : createTexts.submitChallenge}
                  </Button>
                </div>
              </aside>
            </div>
          </form>
        </AdminSection>
      </div>
    </AppLayout>
  );
}
